export type RichClipboardContent = {
  /** Pasted by editors that understand tables and formatting, such as Google Docs, Sheets, and Word. */
  html: string;
  /** Pasted by everything else. */
  text: string;
};

/**
 * Copies formatted content with a plain-text fallback. The content may still be loading: the clipboard write
 * starts inside the click, so browsers that require a user gesture (Safari) accept a payload that resolves later.
 */
export async function copyRichContentToClipboard(
  content: RichClipboardContent | Promise<RichClipboardContent>
): Promise<void> {
  const pending = Promise.resolve(content);

  if (typeof ClipboardItem !== "undefined" && typeof navigator.clipboard?.write === "function") {
    const html = pending.then((loaded) => new Blob([loaded.html], { type: "text/html" }));
    const text = pending.then((loaded) => new Blob([loaded.text], { type: "text/plain" }));
    // The write reports a failed load; these keep the branches the browser never reads from surfacing as unhandled.
    html.catch(() => undefined);
    text.catch(() => undefined);

    try {
      await navigator.clipboard.write([new ClipboardItem({ "text/html": html, "text/plain": text })]);
      return;
    } catch {
      // Fall through to plain text when rich writes are blocked; a failed load rejects again below.
    }
  }

  const { text } = await pending;
  await copyTextToClipboard(text);
}

export async function copyTextToClipboard(text: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
    return;
  } catch {
    // Clipboard API requires a focused document; fall back for unfocused windows/iframes.
  }

  const textArea = document.createElement("textarea");
  textArea.value = text;
  textArea.setAttribute("readonly", "");
  textArea.style.position = "fixed";
  textArea.style.top = "0";
  textArea.style.left = "0";
  textArea.style.opacity = "0";
  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();
  textArea.setSelectionRange(0, text.length);

  try {
    const succeeded = document.execCommand("copy");
    if (!succeeded) {
      throw new Error("Failed to copy text");
    }
  } finally {
    textArea.remove();
  }
}
